/**
 * Conter caminho dentro de raiz — uma pergunta, uma resposta.
 *
 * Isto vivia em três lugares (API, daemon e painel) e **uma das cópias estava
 * errada**: a do painel comparava por prefixo cru, então `/home/claude/projetos`
 * dava como sua a pasta vizinha `/home/claude/projetos-secretos`. O daemon e a
 * API recusavam depois, mas o explorador já tinha oferecido o `..` e a tela de
 * sessão nova já tinha escolhido a máquina errada.
 *
 * Sem `node:path` de propósito: o painel importa isto no navegador. Quem
 * precisa resolver link simbólico, `..` ou nome curto do Windows resolve
 * **antes** e passa o caminho já real — é o que o daemon faz.
 */

/** Raiz liberada; só o caminho importa para conter. */
export interface ComCaminho {
  caminho: string;
}

/**
 * Tira as barras do fim e baixa a caixa.
 *
 * A comparação não diferencia maiúsculas porque o Windows também não. No
 * Linux isso torna a checagem mais frouxa do que precisaria ser — é o preço
 * de uma função só para os dois sistemas, e o lado seguro é o daemon, que
 * ainda confere o caminho real contra o disco.
 */
function chave(caminho: string): string {
  return caminho.replace(/[\\/]+$/, '').toLowerCase();
}

/**
 * O caminho é a própria raiz, ou está dentro dela?
 *
 * Exige o separador na fronteira: sem isso `/home/claudeX` passaria por
 * `/home/claude`.
 */
export function dentroDaRaiz(caminho: string, raiz: string): boolean {
  const base = chave(raiz);
  if (base.length === 0) return false;

  const alvo = chave(caminho);
  if (alvo.length === 0) return false;
  if (alvo === base) return true;

  return alvo.startsWith(base) && /[\\/]/.test(alvo.charAt(base.length));
}

/** A primeira raiz que contém o caminho, ou `undefined`. */
export function raizQueContem<T extends ComCaminho>(caminho: string, raizes: T[]): T | undefined {
  return raizes.find((raiz) => dentroDaRaiz(caminho, raiz.caminho));
}

/** Alguma das raízes contém o caminho? */
export function dentroDeAlgumaRaiz(caminho: string, raizes: ComCaminho[]): boolean {
  return raizQueContem(caminho, raizes) !== undefined;
}
